import {
  CsrLocalBundle,
  CsrRemoteBundle,
  FeatureServiceLoaderContext,
  LocalBundleArgs,
  RemoteBundleArgs,
  csrLocalBundle,
  csrRemoteBundle,
  defineLocalFeatureServices,
  defineRemoteFeatureServices,
} from '@oneaudi/feature-services';
import {
  FeatureServiceProviderDefinition,
  FeatureServices,
  Logger,
  SharedFeatureService,
} from '@feature-hub/core';
import {
  getAuthOptions,
  getCarlineLinkServiceConfig,
  getColaOptions,
  getConfiguratorLinkOptions,
  getCookieServiceOption,
  getCurrentCarlineOptions,
  getDisclaimerManagerOptions,
  getEnvConfigOptions,
  getGfaServiceConfig,
  getI18nOptions,
  getLocaleOptions,
  getMarketContextServiceOptions,
  getNavigationOptions,
  getOneGraphOptions,
  getRenderModeOptions,
  getStateManagerConfig,
  getTrackingOptions,
  getVueFormatterOptions,
  prepareWindow,
} from './service-configuration';

import { ContentStore } from '@oneaudi/content-service';
import { createLogger } from '../logger';
import { loadAmdModule } from '@feature-hub/module-loader-amd';
import { rootLocationTransformer } from './service-configuration-history';

export type FeatureServiceConfigOverride = Partial<
  LocalBundleArgs<CsrLocalBundle> & RemoteBundleArgs<CsrRemoteBundle>
>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare global {
  interface Window {
    SETUPS: {
      get(string): string;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    featureServiceDefinitions: Array<FeatureServiceProviderDefinition<any>>;
    featureServiceConfigs?: FeatureServiceConfigOverride;
    digitalData: Record<string, unknown>;
  }
}

const featureServiceDefinitions = async (
  contentStore: ContentStore,
  logger: Logger,
): Promise<
  FeatureServiceProviderDefinition<SharedFeatureService, FeatureServices>[]
> => {
  prepareWindow();

  const localServiceArgs: LocalBundleArgs<CsrLocalBundle> = {
    'audi:configuratorStateManager': [getStateManagerConfig(document.head)],
    'audi:envConfigService': [getEnvConfigOptions(document.head)],
    'audi-carline-link-service': [getCarlineLinkServiceConfig(document.head)],
    'audi-cola-query-service': [],
    'audi-cola-service': [getColaOptions(document.head)],
    'audi-content-service': [contentStore],
    'cookie-service': [getCookieServiceOption()],
    'gfa:cart-service': [{}],
    'gfa:service-config-provider': [getGfaServiceConfig()],
    'locale-service': [getLocaleOptions(document.head)],
    'onegraph-query-service': [],
    'onegraph-service': [getOneGraphOptions(document.head)],
    'oneshop:apollo-service': [{}],
    'oneshop:tenant-service': [{}],
    'oneshop:user-session-service': [{}],
    's2:history': [rootLocationTransformer],
    's2:logger': [createLogger],
    'user-vehicle-feature-service': [],
    'vw:authService': [getAuthOptions(document.head)],
    ...window.featureServiceConfigs,
  };

  const remoteServiceArgs: RemoteBundleArgs<CsrRemoteBundle> = {
    'audi:configuratorInPageNavigation': [],
    'audi-audicode-service': [],
    'audi-configurator-data-service': [],
    'audi-configurator-footnote-service': [],
    'audi-configurator-link-service': [
      getConfiguratorLinkOptions(document.head),
    ],
    'audi-current-carline-service': [getCurrentCarlineOptions(document.head)],
    'audi-footnote-reference-service': [],
    'audi-market-context-service': [
      getMarketContextServiceOptions(document.head),
    ],
    'audi-personalization-service': [],
    'audi-render-mode-service': [getRenderModeOptions()],
    'audi-tracking-service': [getTrackingOptions()],
    'audi-video-service': [],
    'dbad:audi-i18n-service': [getI18nOptions(document.head)],
    'dbad:audi-vue-formatter-service': [getVueFormatterOptions(document.head)],
    'disclaimer-manager': [getDisclaimerManagerOptions()],
    'navigation-service': [getNavigationOptions()],
    ...window.featureServiceConfigs,
  };

  const localServices = defineLocalFeatureServices(
    csrLocalBundle,
    localServiceArgs,
    logger,
  );

  const remoteServices = await defineRemoteFeatureServices(
    csrRemoteBundle,
    remoteServiceArgs,
    loadAmdModule,
    logger,
    window as unknown as FeatureServiceLoaderContext,
  );

  const serviceDefinitions = [
    ...localServices,
    ...remoteServices,
    ...window.featureServiceDefinitions,
  ];

  return serviceDefinitions;
};

export { featureServiceDefinitions };
